'use client';

import { useRef, useState } from 'react';
import {
  DEFAULT_HOTEL_SORT,
  getDefaultDistance,
  useSearchHotelStore,
} from '@store/search-hotel-store';
import { hotelDistanceLabel } from '../hotel-distance';
import { SortByDropdown } from '@v2/components/SortByDropdown/SortByDropdown';
import {
  Button,
  Check,
  Checkbox,
  ChevronDown,
  Close,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  SearchMD,
} from '@v2/ui';
import { cn } from '@v2/utils';
import {
  DistanceToVenueField,
  PriceTravelerRangeField,
  RatingField,
  type PriceRangeActions,
} from './Filters';
import { HOTEL_SORT_OPTIONS, HOTEL_FEATURES_LIST } from './data';
import { useGetHotels } from '../hooks/use-hotels-list';
import { invalidatePropertySearch } from '../util';
import {
  HotelFilterActions,
  HotelFilterSheet,
  useIsDesktopHotelFilters,
} from './HotelFilterSheet';

const filterPill =
  'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3.5 text-sm font-medium text-gray-700 hover:border-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 data-[state=open]:border-primary-300';
const activePill = 'border-primary-200 bg-primary-50 text-primary-600';

function pillClass(active: boolean) {
  return cn(filterPill, active && [activePill, 'pr-10']);
}

function ResetFilter({
  label,
  onReset,
}: {
  label: string;
  onReset: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onReset}
      className="-ml-9 mr-1 flex size-8 shrink-0 items-center justify-center rounded-full text-primary-600 hover:bg-primary-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
    >
      <Close size="14" />
    </button>
  );
}

export function HotelSearchField({
  className,
  placeholder = 'Search by hotel name',
}: {
  className?: string;
  placeholder?: string;
}) {
  const query = useSearchHotelStore((state) => state.hotelFilters.query);
  return (
    <Input
      type="search"
      aria-label="Search by hotel name"
      placeholder={placeholder}
      value={query}
      onChange={(event) =>
        useSearchHotelStore
          .getState()
          .setHotelFilters({ query: event.target.value })
      }
      startIcon={<SearchMD size="20" className="text-gray-400" />}
      className={cn('h-11 rounded-xl bg-white text-sm', className)}
    />
  );
}

function SortFilterChip() {
  const isDesktop = useIsDesktopHotelFilters();
  const [open, setOpen] = useState(false);
  const sortBy =
    useSearchHotelStore((state) => state.hotelFilters.sortBy) ||
    DEFAULT_HOTEL_SORT;
  const isSortActive = Boolean(sortBy && sortBy !== DEFAULT_HOTEL_SORT);
  const sortLabel =
    HOTEL_SORT_OPTIONS.find((option) => option.value === sortBy)?.name ??
    'Distance to search location';
  const selectSort = (value: string) => {
    useSearchHotelStore.getState().setHotelFilters({
      sortBy: value === sortBy ? DEFAULT_HOTEL_SORT : value,
    });
  };
  const trigger = (
    <button type="button" className={pillClass(isSortActive)}>
      <span>{sortLabel}</span>
      {!isSortActive && <ChevronDown size="16" />}
    </button>
  );

  return (
    <div className="flex shrink-0 items-center">
      {isDesktop ? (
        <SortByDropdown
          options={HOTEL_SORT_OPTIONS}
          value={sortBy}
          onSelect={selectSort}
          triggerClassName={pillClass(isSortActive)}
        >
          <span>{sortLabel}</span>
          {!isSortActive && <ChevronDown size="16" />}
        </SortByDropdown>
      ) : (
        <HotelFilterSheet
          open={open}
          onOpenChange={setOpen}
          title="Sort by"
          trigger={trigger}
        >
          <div
            className="flex flex-col gap-2"
            role="listbox"
            aria-label="Sort by"
          >
            {HOTEL_SORT_OPTIONS.map((option) => {
              const selected = sortBy === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    selectSort(option.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex min-h-12 w-full items-center justify-between rounded-xl border-2 px-4 text-left text-sm font-semibold transition-[border-color,background-color,color] duration-200',
                    selected
                      ? 'border-primary-500 bg-primary-50/30 text-primary-600'
                      : 'border-gray-200 text-gray-950 hover:border-primary-500 hover:bg-primary-50'
                  )}
                >
                  {option.name}
                  {selected ? (
                    <Check
                      size="18"
                      className="text-primary-600"
                      pathProps={{ stroke: 'currentColor' }}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </HotelFilterSheet>
      )}
      {isSortActive && (
        <ResetFilter
          label="Reset sort"
          onReset={() =>
            useSearchHotelStore
              .getState()
              .setHotelFilters({ sortBy: DEFAULT_HOTEL_SORT })
          }
        />
      )}
    </div>
  );
}

function DistanceFilterChip() {
  const isDesktop = useIsDesktopHotelFilters();
  const [open, setOpen] = useState(false);
  const distance = useSearchHotelStore((state) => state.hotelFilters.distance);
  const hasDistanceChanged = useSearchHotelStore(
    (state) => state.hasDistanceChanged
  );
  const [draft, setDraft] = useState(distance);
  const resetDistance = () => {
    useSearchHotelStore.getState().setHotelFilters({
      distance: getDefaultDistance(),
    });
    useSearchHotelStore.setState({ hasDistanceChanged: false });
    invalidatePropertySearch();
  };

  return (
    <div className="flex shrink-0 items-center">
      <HotelFilterSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setDraft(distance);
          setOpen(nextOpen);
        }}
        title="Distance to search location"
        ariaLabel="Distance to search location filter"
        trigger={
          <button type="button" className={pillClass(hasDistanceChanged)}>
            {hotelDistanceLabel(distance)}
            {!hasDistanceChanged && <ChevronDown size="16" />}
          </button>
        }
        footer={
          isDesktop ? undefined : (
            <HotelFilterActions
              layout="sheet"
              resetDisabled={draft[0] === getDefaultDistance()[0]}
              onReset={() => setDraft(getDefaultDistance())}
              onApply={() => {
                if (draft[0] === getDefaultDistance()[0]) resetDistance();
                else
                  useSearchHotelStore.getState().setHotelRange({
                    distance: draft,
                  });
                setOpen(false);
              }}
            />
          )
        }
      >
        <DistanceToVenueField
          hideLabel={!isDesktop}
          value={isDesktop ? distance : draft}
          onValueChange={
            isDesktop
              ? (next) =>
                  useSearchHotelStore.getState().setHotelRange({
                    distance: next,
                  })
              : setDraft
          }
        />
      </HotelFilterSheet>
      {hasDistanceChanged && (
        <ResetFilter label="Reset distance" onReset={resetDistance} />
      )}
    </div>
  );
}

function RatingFilterChip() {
  const isDesktop = useIsDesktopHotelFilters();
  const filters = useSearchHotelStore((state) => state.hotelFilters);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(filters.rating);
  const hasRatingFilter = filters.rating.length > 0;
  const applyRating = () => {
    useSearchHotelStore.getState().setHotelFilters({ rating });
    setOpen(false);
  };
  const resetDraft = () => setRating([]);

  return (
    <div className="flex shrink-0 items-center">
      <HotelFilterSheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setRating(filters.rating);
          setOpen(nextOpen);
        }}
        title="Filter by star rating"
        description={isDesktop ? undefined : 'Select the minimum star rating'}
        ariaLabel="Star rating filter"
        contentClassName="w-[min(340px,calc(100vw-32px))]"
        trigger={
          <button type="button" className={pillClass(hasRatingFilter)}>
            {hasRatingFilter
              ? `${Math.min(...filters.rating.map(Number))} stars & up`
              : 'Rating'}
            {!hasRatingFilter && <ChevronDown size="16" />}
          </button>
        }
        footer={
          <HotelFilterActions
            layout={isDesktop ? 'popover' : 'sheet'}
            onReset={resetDraft}
            onApply={applyRating}
          />
        }
      >
        <RatingField
          hideLabel={!isDesktop}
          value={rating}
          onValueChange={setRating}
        />
      </HotelFilterSheet>
      {hasRatingFilter && (
        <ResetFilter
          label="Clear rating"
          onReset={() => {
            setRating([]);
            useSearchHotelStore.getState().setHotelFilters({ rating: [] });
          }}
        />
      )}
    </div>
  );
}

function PriceFilterChip() {
  const isDesktop = useIsDesktopHotelFilters();
  const [open, setOpen] = useState(false);
  const pricePerTraveler = useSearchHotelStore(
    (state) => state.hotelFilters.pricePerTraveler
  );
  const hasPriceChanged = useSearchHotelStore(
    (state) => state.hasPricePerTravelerChanged
  );
  const { maxHotelPrice } = useGetHotels();
  const priceActions = useRef<PriceRangeActions>(null);
  const resetPrice = () => {
    useSearchHotelStore
      .getState()
      .setHotelFilters({ pricePerTraveler: [0, maxHotelPrice] });
    useSearchHotelStore.setState({ hasPricePerTravelerChanged: false });
  };
  const commitPrice = (next: number[]) => {
    if (next[0] === 0 && next[1] === maxHotelPrice) resetPrice();
    else
      useSearchHotelStore.getState().setHotelRange({ pricePerTraveler: next });
    setOpen(false);
  };

  return (
    <div className="flex shrink-0 items-center">
      <HotelFilterSheet
        open={open}
        onOpenChange={setOpen}
        title="Price"
        ariaLabel="Price filter"
        trigger={
          <button type="button" className={pillClass(hasPriceChanged)}>
            Price{!hasPriceChanged && <ChevronDown size="16" />}
          </button>
        }
        footer={
          isDesktop ? undefined : (
            <HotelFilterActions
              layout="sheet"
              onReset={() => priceActions.current?.reset()}
              onApply={() => priceActions.current?.apply()}
            />
          )
        }
      >
        <PriceTravelerRangeField
          staged
          hideActions={!isDesktop}
          actionRef={priceActions}
          value={pricePerTraveler}
          onValueChange={commitPrice}
        />
      </HotelFilterSheet>
      {hasPriceChanged && (
        <ResetFilter label="Reset price" onReset={resetPrice} />
      )}
    </div>
  );
}

function AmenitiesFilterChip() {
  const isDesktop = useIsDesktopHotelFilters();
  const [open, setOpen] = useState(false);
  const featureHotel = useSearchHotelStore(
    (state) => state.hotelFilters.featureHotel
  );
  const hasAmenitiesFilter = featureHotel.length > 0;
  const setFeatureHotel = (next: string[]) => {
    useSearchHotelStore.getState().setHotelFilters({ featureHotel: next });
  };
  const toggleFeature = (value: string, checked: boolean) => {
    const current = useSearchHotelStore.getState().hotelFilters.featureHotel;
    setFeatureHotel(
      checked ? [...current, value] : current.filter((item) => item !== value)
    );
  };
  const trigger = (
    <button type="button" className={pillClass(hasAmenitiesFilter)}>
      {hasAmenitiesFilter ? `Amenities (${featureHotel.length})` : 'Amenities'}
      {!hasAmenitiesFilter && <ChevronDown size="16" />}
    </button>
  );

  return (
    <div className="flex shrink-0 items-center">
      {isDesktop ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[200px] rounded-2xl p-2"
          >
            {HOTEL_FEATURES_LIST.map((item) => (
              <DropdownMenuCheckboxItem
                key={item.value}
                checked={featureHotel.includes(item.value)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) =>
                  toggleFeature(item.value, checked === true)
                }
                className="min-h-11 rounded-lg py-3 pl-8 pr-4 text-sm font-medium focus:bg-gray-50 data-[state=checked]:text-primary-600"
              >
                {item.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <HotelFilterSheet
          open={open}
          onOpenChange={setOpen}
          title="Amenities"
          trigger={trigger}
          footer={
            <HotelFilterActions
              layout="sheet"
              applyLabel="Done"
              resetDisabled={!hasAmenitiesFilter}
              onReset={() => setFeatureHotel([])}
              onApply={() => setOpen(false)}
            />
          }
        >
          <div className="flex flex-col">
            {HOTEL_FEATURES_LIST.map((item) => (
              <label
                key={item.value}
                className="flex min-h-12 cursor-pointer items-center gap-3 py-2 text-sm font-semibold text-gray-800"
              >
                <Checkbox
                  size="md"
                  checked={featureHotel.includes(item.value)}
                  onCheckedChange={(checked) =>
                    toggleFeature(item.value, checked === true)
                  }
                />
                {item.icon}
                {item.label}
              </label>
            ))}
          </div>
        </HotelFilterSheet>
      )}
      {hasAmenitiesFilter && (
        <ResetFilter
          label="Clear amenities"
          onReset={() => setFeatureHotel([])}
        />
      )}
    </div>
  );
}

export function TopHotelFilterBar() {
  const filters = useSearchHotelStore((state) => state.hotelFilters);
  const hasDistanceChanged = useSearchHotelStore(
    (state) => state.hasDistanceChanged
  );
  const hasPriceChanged = useSearchHotelStore(
    (state) => state.hasPricePerTravelerChanged
  );
  const { maxHotelPrice } = useGetHotels();
  const isAnyFilterActive =
    hasDistanceChanged ||
    hasPriceChanged ||
    filters.rating.length > 0 ||
    filters.featureHotel.length > 0 ||
    Boolean(filters.sortBy && filters.sortBy !== DEFAULT_HOTEL_SORT) ||
    Boolean(filters.query);

  const resetAll = () => {
    useSearchHotelStore.getState().resetHotelFilters();
    useSearchHotelStore
      .getState()
      .setHotelFilters({ pricePerTraveler: [0, maxHotelPrice] });
    useSearchHotelStore.setState({ hasPricePerTravelerChanged: false });
    invalidatePropertySearch();
  };

  return (
    <div className="no-scrollbar flex flex-nowrap items-center gap-2 overflow-x-auto border-t border-gray-100 px-4 py-3 lg:px-5 lg:py-4">
      <div className="hidden shrink-0 lg:block lg:w-64 xl:w-72">
        <HotelSearchField />
      </div>
      <SortFilterChip />
      <DistanceFilterChip />
      <RatingFilterChip />
      <PriceFilterChip />
      <AmenitiesFilterChip />
      {isAnyFilterActive && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="shrink-0 whitespace-nowrap text-sm text-primary-600"
          onClick={resetAll}
        >
          Clear all
        </Button>
      )}
    </div>
  );
}
